// Leitor de extrato bancário em CSV.
//
// Extraído da importação antiga (Contabilidade → Configurações → Importar) e
// endurecido: campos entre aspas com separador dentro, valor com milhar e
// vírgula decimal, colunas separadas de crédito e débito, datas inválidas.
//
// Devolve { formato: 'csv', banco, bankId, acctId, periodoInicio, periodoFim,
// transacoes: [{ data, descricao, valor, fitid }] } com `valor` com sinal
// (positivo = entrada, negativo = saída). Lança Error com mensagem pronta para
// a tela quando o arquivo não é um extrato reconhecível.

import { normalizarTexto, parseData, parseValor } from './comum';

// Divide uma linha respeitando aspas ("a;b" é um campo só; "" é aspas literal).
export function dividirLinhaCsv(linha, sep) {
  const campos = [];
  let atual = '';
  let emAspas = false;
  for (let i = 0; i < linha.length; i++) {
    const c = linha[i];
    if (emAspas) {
      if (c === '"') {
        if (linha[i + 1] === '"') { atual += '"'; i++; } else { emAspas = false; }
      } else {
        atual += c;
      }
    } else if (c === '"') {
      emAspas = true;
    } else if (c === sep) {
      campos.push(atual.trim());
      atual = '';
    } else {
      atual += c;
    }
  }
  campos.push(atual.trim());
  return campos;
}

function detectarSeparador(linhas) {
  // Conta fora de aspas nas primeiras linhas: o mais frequente vence.
  const amostra = linhas.slice(0, 15).join('\n').replace(/"[^"]*"/g, '');
  const pontoVirgula = (amostra.match(/;/g) || []).length;
  const tab = (amostra.match(/\t/g) || []).length;
  const virgula = (amostra.match(/,/g) || []).length;
  if (pontoVirgula >= virgula && pontoVirgula >= tab && pontoVirgula > 0) return ';';
  if (tab > virgula) return '\t';
  return ',';
}

function detectarBanco(textoCabecalho) {
  const t = normalizarTexto(textoCabecalho);
  if (t.includes('extrato conta corrente') || t.includes('data lancamento')) return 'Banco Inter';
  if (t.includes('santander') || t.includes('cartao master') || t.includes('agencia')) return 'Banco Santander';
  if (t.includes('pagbank') || t.includes('pagseguro')) return 'PagBank';
  if (t.includes('nubank')) return 'Nubank';
  if (t.includes('itau')) return 'Itaú';
  if (t.includes('bradesco')) return 'Bradesco';
  if (t.includes('sicoob')) return 'Sicoob';
  if (t.includes('sicredi')) return 'Sicredi';
  return null;
}

const ehCabecalho = (campos) => {
  const temData = campos.some(c => c.includes('data') || c === 'date');
  const temValor = campos.some(c => c.includes('valor') || c.includes('value') || c.includes('amount')
    || c.includes('credito') || c.includes('debito') || c.includes('entrada') || c.includes('saida'));
  return temData && temValor;
};

export function parseCsv(texto) {
  const bruto = String(texto ?? '').replace(/^﻿/, '');
  const todasLinhas = bruto.split(/\r?\n/).filter(l => l.trim() && !l.trim().startsWith('#'));
  if (todasLinhas.length < 2) {
    throw new Error('O arquivo está vazio ou não contém movimentações.');
  }

  const sep = detectarSeparador(todasLinhas);
  const banco = detectarBanco(todasLinhas.slice(0, 6).join(' '));

  // O cabeçalho pode vir depois de linhas de título (Inter: "Extrato Conta Corrente", conta, período, saldo).
  let iCab = -1;
  for (let i = 0; i < Math.min(todasLinhas.length, 15); i++) {
    if (ehCabecalho(dividirLinhaCsv(todasLinhas[i], sep).map(normalizarTexto))) { iCab = i; break; }
  }
  if (iCab < 0) {
    throw new Error('Não foi possível identificar as colunas de data e valor. Confira se o arquivo tem a linha de cabeçalho.');
  }

  const cab = dividirLinhaCsv(todasLinhas[iCab], sep).map(normalizarTexto);
  const usadas = new Set();
  const col = (...nomes) => {
    for (const n of nomes) { const i = cab.findIndex((h, idx) => !usadas.has(idx) && h.includes(n)); if (i >= 0) return i; }
    return -1;
  };
  const colExata = (...nomes) => {
    for (const n of nomes) { const i = cab.findIndex(h => h === n); if (i >= 0) return i; }
    return -1;
  };

  const iData = col('data', 'date');
  usadas.add(iData);
  const iValor = col('valor', 'value', 'amount', 'moviment');
  usadas.add(iValor);
  const iCredito = iValor < 0 ? col('credito', 'entrada') : -1;
  const iDebito = iValor < 0 ? col('debito', 'saida') : -1;
  // Inter traz "Histórico" (tipo: Pix enviado, Pagamento...) e "Descrição"
  // (favorecido). As duas juntas dão mais chance de a regra reconhecer.
  const iHist = col('historico');
  const iDesc = colExata('descricao', 'description') >= 0
    ? colExata('descricao', 'description')
    : col('descri', 'memo', 'lancamento', 'titulo', 'detalhe');
  const iDoc = col('documento', 'n doc', 'numero');

  if (iData < 0 || (iValor < 0 && (iCredito < 0 || iDebito < 0))) {
    throw new Error('Não foi possível identificar as colunas de data e valor. Confira se o arquivo tem a linha de cabeçalho.');
  }

  const milharComPonto = sep !== ',';
  const transacoes = [];
  todasLinhas.slice(iCab + 1).forEach(linha => {
    const cel = dividirLinhaCsv(linha, sep);
    const data = parseData(cel[iData]);
    if (!data) return; // rodapé, saldo, linha em branco
    let valor;
    if (iValor >= 0) {
      valor = parseValor(cel[iValor], { milharComPonto });
    } else {
      const cr = parseValor(cel[iCredito], { milharComPonto });
      const db = parseValor(cel[iDebito], { milharComPonto });
      valor = (Number.isFinite(cr) ? Math.abs(cr) : 0) - (Number.isFinite(db) ? Math.abs(db) : 0);
    }
    if (!Number.isFinite(valor) || Math.abs(valor) < 0.005) return;

    const hist = iHist >= 0 && iHist !== iDesc ? (cel[iHist] || '').trim() : '';
    const desc = iDesc >= 0 ? (cel[iDesc] || '').trim() : '';
    let descricao = [hist, desc].filter(Boolean).join(' — ');
    if (hist && desc && normalizarTexto(hist) === normalizarTexto(desc)) descricao = desc;
    if (!descricao) descricao = 'Movimentação importada';

    transacoes.push({
      data,
      descricao,
      valor: Math.round(valor * 100) / 100,
      documento: iDoc >= 0 && iDoc !== iDesc && iDoc !== iHist ? (cel[iDoc] || '').trim() : '',
      fitid: null,
    });
  });

  if (transacoes.length === 0) {
    throw new Error('Nenhuma movimentação válida foi encontrada no arquivo.');
  }

  const datas = transacoes.map(t => t.data).sort();
  return {
    formato: 'csv',
    banco,
    bankId: null,
    acctId: null,
    periodoInicio: datas[0],
    periodoFim: datas[datas.length - 1],
    transacoes,
  };
}

export default parseCsv;
